import { useState, useRef, useEffect } from "react";
import { ChevronDown, Check } from "lucide-react";
import { useTranslation } from "../../i18n";
import { useAccess } from "../../access/AccessContext";

// Inline switcher rendered inside the admin-home greeting strip. Lets an
// admin pivot the whole admin section to a different warehouse without
// logging out — writes the choice to localStorage.user.warehouse and
// reloads, since most admin pages read the warehouse once at mount and
// aren't reactive to a live change.
//
// The list is hard-coded against the warehouses currently used across
// the app (Newport, Pembroke, Poland). If a fourth warehouse is
// added, surface it here too.
//
// Who gets the dropdown (D14, 2026-09-25): only a caller holding the
// `cross_warehouse` grant (read or write) — the BE refuses every
// other-warehouse request without it, so offering the switch would only
// produce a page of failed tiles. Everyone else sees their warehouse as a
// static label. Admin holds every key, so nothing changes for them.

const WAREHOUSES = ["Newport", "Pembroke", "Poland"] as const;
type Warehouse = (typeof WAREHOUSES)[number];

const AdminWarehouseSwitcher = () => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const userData = typeof window !== "undefined" ? window.localStorage.getItem("user") : null;
    const userObject = userData !== null ? JSON.parse(userData) : { warehouse: "" };
    const current: string = userObject.warehouse || "";
    const { authoritative, hasAccess } = useAccess();
    // Until the access map has settled, show the label only — never a
    // dropdown that the next frame would take away.
    const canSwitch = authoritative && hasAccess("cross_warehouse");

    useEffect(() => {
        const onDown = (e: MouseEvent) => {
            if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, []);

    if (!current) return null;
    if (!canSwitch) {
        return (
            <span className="admin-warehouse-switcher admin-warehouse-switcher--locked">
                <span className="admin-warehouse-switcher-current">{current}</span>
            </span>
        );
    }

    const select = (w: Warehouse) => {
        if (w === current) {
            setOpen(false);
            return;
        }
        // Persist the new warehouse, then reload so every admin sub-page
        // re-reads localStorage on mount with the new value.
        const next = { ...userObject, warehouse: w };
        try { window.localStorage.setItem("user", JSON.stringify(next)); } catch { /* ignore */ }
        window.location.reload();
    };

    return (
        <span className="admin-warehouse-switcher" ref={wrapperRef}>
            <button
                type="button"
                className="admin-warehouse-switcher-button"
                onClick={() => setOpen(o => !o)}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={t("switch_warehouse")}
            >
                <span className="admin-warehouse-switcher-current">{current}</span>
                <ChevronDown size={14} strokeWidth={1.8} className="admin-warehouse-switcher-chev" />
            </button>
            {open && (
                <div className="admin-warehouse-switcher-menu" role="listbox">
                    {WAREHOUSES.map(w => (
                        <button
                            key={w}
                            type="button"
                            role="option"
                            aria-selected={w === current}
                            className={`admin-warehouse-switcher-item${w === current ? " admin-warehouse-switcher-item--active" : ""}`}
                            onClick={() => select(w)}
                        >
                            <span>{w}</span>
                            {w === current && <Check size={14} strokeWidth={2} />}
                        </button>
                    ))}
                </div>
            )}
        </span>
    );
};

export default AdminWarehouseSwitcher;
